const SAMPLE_VALUES_BY_KEY = {
  status: ['Active', 'Pending', 'Completed'],
  inbox: ['Email', 'Live Chat', 'WhatsApp'],
  user: ['Alice', 'Bob', 'Charlie'],
  agent: ['Alice', 'Bob', 'Charlie'],
  contact: ['Customer A', 'Customer B', 'Customer C'],
  conversation: ['#1024', '#1042', '#1108'],
  country: ['US', 'UK', 'DE'],
  channel: ['Email', 'Chat', 'Phone'],
  type: ['Type A', 'Type B', 'Type C'],
  category: ['Premium', 'Standard', 'Basic'],
  plan: ['Premium', 'Standard', 'Basic'],
};

// How many buckets to fabricate per granularity so the preview stays compact.
const PERIOD_COUNT = { hour: 8, day: 7, week: 6, month: 6, year: 4 };

function dimensionKey(dimension) {
  return dimension?.property || dimension?.source || '';
}

function sampleDimensionValues(dimension) {
  const key = dimensionKey(dimension).toLowerCase();
  return SAMPLE_VALUES_BY_KEY[key] || ['Group A', 'Group B', 'Group C'];
}

// Returns ISO timestamps (oldest first) for the given granularity, so the
// preview can format them with the same helper the real report uses.
function generatePeriodDates(granularity) {
  const count = PERIOD_COUNT[granularity] || 7;
  const dates = [];
  const now = new Date();
  for (let i = count - 1; i >= 0; i -= 1) {
    let d;
    // Month/year buckets are anchored to the period start: subtracting months from
    // "today" overflows on month-end (May 31 - 3 months -> "Feb 31" -> Mar 3),
    // producing duplicate and skipped labels.
    if (granularity === 'year') {
      d = new Date(now.getFullYear() - i, 0, 1);
    } else if (granularity === 'month') {
      d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    } else {
      d = new Date(now);
      if (granularity === 'week') d.setDate(d.getDate() - i * 7);
      else if (granularity === 'hour') d.setHours(d.getHours() - i);
      else d.setDate(d.getDate() - i);
    }
    dates.push(d.toISOString());
  }
  return dates;
}

function generateMetricValue(metric) {
  const { aggregation } = metric;
  if (aggregation === 'count') {
    return Math.floor(Math.random() * 80) + 10;
  }
  if (aggregation === 'avg') {
    return Math.round((Math.random() * 200 + 20) * 100) / 100;
  }
  return Math.floor(Math.random() * 5000) + 100;
}

function aggregateValues(aggregation, values) {
  if (!values.length) return 0;
  switch (aggregation) {
    case 'count':
    case 'sum':
      return values.reduce((a, b) => a + b, 0);
    case 'avg': {
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      return Math.round(avg * 100) / 100;
    }
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    default:
      return values.reduce((a, b) => a + b, 0);
  }
}

function withMetrics(row, metrics) {
  metrics.forEach(metric => {
    row[metric.key] = generateMetricValue(metric);
  });
  return row;
}

// Produces sample rows in the same shape the query engine returns, so the
// preview can be rendered by the shared report chart helpers. Honours the
// configured time granularity ("none" disables time bucketing) and dimension.
export function generateSampleData(
  metrics,
  dimensions = [],
  granularity = 'day'
) {
  const primaryDimension = dimensions?.[0];
  const hasDimension = !!primaryDimension;
  const dimensionValues = hasDimension
    ? sampleDimensionValues(primaryDimension)
    : [];
  const noTime = granularity === 'none';

  let timeseries;
  if (noTime && hasDimension) {
    timeseries = dimensionValues.map(value =>
      withMetrics({ dimension_value: value }, metrics)
    );
  } else if (noTime) {
    timeseries = [withMetrics({}, metrics)];
  } else if (hasDimension) {
    timeseries = [];
    generatePeriodDates(granularity).forEach(period => {
      dimensionValues.forEach(value => {
        timeseries.push(
          withMetrics({ period, dimension_value: value }, metrics)
        );
      });
    });
  } else {
    timeseries = generatePeriodDates(granularity).map(period =>
      withMetrics({ period }, metrics)
    );
  }

  const summary = {};
  metrics.forEach(metric => {
    const values = timeseries.map(row => row[metric.key]);
    summary[metric.key] = aggregateValues(metric.aggregation, values);
  });

  return {
    timeseries,
    summary,
    hasDimension,
    dimensionLabel:
      primaryDimension?.label || dimensionKey(primaryDimension) || '',
  };
}
